<script setup lang="ts">
import authApi from '@/services/authApi';
import { useDialogStore } from '@/stores/dialog';
import { usePopStore } from '@/stores/pop';
import { useUserStore } from '@/stores/user';
import SvgIcon from '../base/SvgIcon.vue';
import WrongLogin from './errorAlert/WrongLogin.vue';
import FormCard from './formElement/FormCard.vue';
import FormInput from './formElement/FormInput.vue';
import AppleThirdPart from './thirdpart/AppleThirdPart.vue';
import GoogleThirdPart from './thirdpart/GoogleThirdPart.vue';
import LineThirdParty from './thirdpart/LineThirdParty.vue';
import MetaThirdParty from './thirdpart/MetaThirdParty.vue';

const props = withDefaults(
  defineProps<{
    showClose?: boolean;
  }>(),
  {
    showClose: true
  }
);
const user = useUserStore();
const popStore = usePopStore();
const emit = defineEmits(['getMemberDataAndLogin']);
const formChoose = defineModel('formChoose');
const showPopup = defineModel('showPopup');
const showAlert = defineModel('showAlert');
const { isOpen, text, resolveAction } = storeToRefs(useDialogStore());
const { googleVerifyHandler, metaVerifyHandler } = useThirdPartVerify();

const loginData = ref({
  username: user.userAccount,
  password: user.userPassword
});

const login = async () => {
  popStore.loadingStatus = true;
  if (
    loginData.value.username.trim() === '' ||
    loginData.value.username === undefined ||
    loginData.value.username === null
  ) {
    return;
  }
  const res = await authApi.login(loginData.value);
  if (typeof res === 'string') {
    user.token = res;
    saveUserAccountAndPasswordOrNot(user.rememberAccountAndPassword);
    emit('getMemberDataAndLogin', res);
  }
  if (res.status && res.status !== 200) {
    switch (res.errorCode) {
      case -4001:
        showPopup.value = true;
        showAlert.value = WrongLogin;
        break;
    }
  }
  popStore.loadingStatus = false;
};
const saveUserAccountAndPasswordOrNot = (save: boolean) => {
  if (save) {
    user.userAccount = loginData.value.username;
    user.userPassword = loginData.value.password;
  } else {
    user.userAccount = '';
    user.userPassword = '';
  }
};
const ForgetAccount = () => {
  text.value = {
    title: '忘記帳號',
    content: '請聯繫客服：02-2523-9777 或\nLine：@funday',
    actionBtn: '確定',
    contentNeedCenter: true
  };
  resolveAction.value = () => {};

  isOpen.value = true;
};
const ForgetPassword = () => {
  formChoose.value = 'ForgetPassword';
};

const handleGoogleVerify = (token: string) => {
  googleVerifyHandler(token);
  formChoose.value = 'ThirdPartSignupForm';
};

const handleMetaVerify = (token: string) => {
  metaVerifyHandler(token);
  formChoose.value = 'ThirdPartSignupForm';
};

const isBrowser = () => {
  const userAgent = navigator.userAgent;

  // 判断用户代理字符串中是否包含特定的浏览器标识
  if (userAgent.indexOf('Chrome') !== -1) {
    return true;
  } else if (userAgent.indexOf('Safari') !== -1) {
    return true;
  } else if (userAgent.indexOf('Firefox') !== -1) {
    return true;
  } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) {
    return true;
  } else if (userAgent.indexOf('Edge') !== -1) {
    return true;
  } else if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) {
    return true;
  } else {
    return false; // 如果无法确定浏览器类型，则返回 "Unknown"
  }
};
</script>

<template>
  <FormCard @keyup.enter="login">
    <div class="title-block">
      <!-- 標題 -->
      <slot name="title"></slot>
      <div v-if="props.showClose" class="cross-btn" @click="formChoose = ''">
        <SvgIcon color="grey" name="cross" />
      </div>
    </div>
    <FormInput
      :required="true"
      inputPlaceHolder="請輸入帳號"
      inputTitle="帳號"
      inputType="text"
      v-model:inputValue="loginData.username"
    ></FormInput>
    <FormInput
      :canShowInputValue="true"
      :required="true"
      inputPlaceHolder="請輸入密碼"
      inputTitle="密碼"
      inputType="password"
      v-model:inputValue="loginData.password"
    ></FormInput>
    <div class="memory-and-find-password-block">
      <div class="memory-password-block">
        <input id="remember-me" type="checkbox" v-model="user.rememberAccountAndPassword" />
        <label class="remember-me" for="remember-me">
          <div class="virtual-square"></div>
          <p class="text">記住帳號密碼</p>
        </label>
      </div>
      <div class="flex gap-[12px]">
        <p @click="ForgetAccount" class="forget-password-block">忘記帳號</p>
        <p @click="ForgetPassword" class="forget-password-block">忘記密碼</p>
      </div>
    </div>
    <div class="login-btn" @click="login">登入</div>
    <div class="third-part-block">
      <div class="virtual-line"></div>
      <p>或由第三方登入</p>
      <div class="virtual-line"></div>
    </div>
    <div class="third-part-icon-block" v-if="isBrowser()">
      <GoogleThirdPart @googleVerify="handleGoogleVerify" />
      <MetaThirdParty @metaVerify="handleMetaVerify" />
      <LineThirdParty />
      <AppleThirdPart />
    </div>
  </FormCard>
</template>

<style scoped lang="scss">
.title-block {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  .text {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0;
    color: #000000cc;
    font-size: 18px;
    font-weight: 600;
  }
  .cross-btn {
    // position: absolute;
    // right: 12px;
    cursor: pointer;
  }
}
.hint-block {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  gap: 8px;
  margin: 4px 0 16px 0;
  .hint-que {
    color: #00000066;
  }
  .hint-join {
    color: #f74768;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      text-decoration-color: #f74768;
    }
  }
}
.memory-and-find-password-block {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  .forget-password-block {
    color: #f74768;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      text-decoration-color: #f74768;
    }
  }
  .memory-password-block {
    display: flex;
    align-items: center;

    input {
      display: none;
    }
    .remember-me {
      display: flex;
      align-items: center;
      font-size: 16px;
      color: $txt-color-grey-60;
      cursor: pointer;
      .virtual-square {
        width: 20px;
        height: 20px;
        overflow: hidden;
        background-image: url('../../assets/icon/checkboxUncheck.svg');
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
      }
      &:hover {
        color: $txt-color-grey-80;
        .virtual-square {
          background-image: url('../../assets/icon/checkboxHover.svg');
        }
      }
    }
    input:checked ~ .remember-me {
      .virtual-square {
        background-image: url('../../assets/icon/checkboxChecked.svg');
      }
      &:hover {
        .virtual-square {
          background-image: url('../../assets/icon/checkboxCheckedAndHover.svg');
        }
      }
    }
  }
}

.junior-landing-page > .main-content > .card > .login-btn {
  background-color: #d44934;
}

.junior-landing-page > .main-content > .card > .login-btn:hover {
  background-color: #7f2c1f;
}

.login-btn {
  margin: 16px 0;
  background-color: #3a3a3a;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #ffffff;
  height: 40px;
  width: 100%;
  cursor: pointer;
}
.third-part-block {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
  .virtual-line {
    height: 1px;
    flex: 1;
    border: 1px solid #0000001a;
  }
  p {
    color: #00000066;
    font-size: 12px;
  }
}
.third-part-icon-block {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}
</style>
